// This file contains common types, constants, etc. used across the viewport-based and
// element-based context providers.
import { createStore } from 'zustand';
import { FALLBACK_BREAKPOINT } from './fallbackMatchMediaUtils';
/**
 * Simple constant for checking on the list of breakpoints in some rare cases where we need to do vanilla JS.
 * Unfortunately, MUI requires interacting with hooks to get the theme breakpoints, which would be really annoying in
 * some cases where we just want pure JS (e.g. `isResponsiveProp` type guard, fallbacks for testing in cases where the
 * theme provider might be missing).
 *
 *  Defining a simple mapping here and enforcing the entries via types to help us work around these edge cases.
 */ export const breakpointKeyExists = {
    xs: true,
    sm: true,
    md: true,
    lg: true,
    xl: true
};
/**
 * Array of breakpoint keys that should be treated as part of a "mobile" experience.
 */ const MOBILE_BREAKPOINTS = [
    'xs',
    'sm'
];
export const isMobileBreakpoint = (breakpoint)=>{
    return MOBILE_BREAKPOINTS.includes(breakpoint);
};
/**
 * Type guard for differentiating the types of breakpoint state depending on the status.
 */ export const isBreakpointsStoreLoaded = (breakpointsStore)=>{
    return !breakpointsStore.isLoading;
};
/**
 * Generates updated values for a breakpoints store with a new active breakpoint.
 *
 *  @example
 * // Example of updated state for the store when the active breakpoint is `sm`
 * {
 *    xs: false,
 *    sm: true,
 *    md: false,
 *    lg: false,
 *    xl: false,
 *    activeBreakpoint: 'sm',
 *    isMobile: true,
 * };
 */ export function setActiveBreakpoint(breakpoints, activeBreakpoint) {
    // Re-keying only on the active breakpoint changing to avoid too many event triggers on transitions
    // (one for the prior breakpoint going "false" and one for the new breakpoint going "true") that lead to
    // too many re-renders and odd behavior.
    const keyedMatches = breakpoints.keys.reduce((result, breakpointKey)=>{
        return {
            ...result,
            [breakpointKey]: breakpointKey === activeBreakpoint
        };
    }, {});
    return {
        isLoading: false,
        ...keyedMatches,
        activeBreakpoint,
        isMobile: isMobileBreakpoint(activeBreakpoint)
    };
}
/**
 * Returns a very simplistic store that will be returned ONLY in test environments when the `ViewportBreakpointsProvider`
 * is missing. This makes it easier for consumers to avoid needing the provider in tests where they do not care about
 * testing responsive behavior.
 *
 * This store is not dynamic and always has the active breakpoint set to `FALLBACK_BREAKPOINT`.
 */ export const getFallbackBreakpointsStore = ()=>{
    return createStore()(()=>{
        return getFallbackBreakpointsStoreInitState();
    });
};
export const getFallbackBreakpointsStoreInitState = ()=>{
    const fallbackBreakpoints = Object.keys(breakpointKeyExists).reduce((result, breakpointKey)=>{
        return {
            ...result,
            [breakpointKey]: breakpointKey === FALLBACK_BREAKPOINT
        };
    }, {});
    return {
        isLoading: false,
        ...fallbackBreakpoints,
        activeBreakpoint: FALLBACK_BREAKPOINT,
        isMobile: isMobileBreakpoint(FALLBACK_BREAKPOINT),
        setActiveBreakpoint: ()=>{
        // noop
        }
    };
};

//# sourceMappingURL=breakpoint-provider-model.js.map